import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { initSentry, SentryErrorBoundary, FallbackUI } from "./lib/sentry";
import {
  clearChunkReloadFlag,
  isChunkLoadError,
  reloadOnceForNewVersion,
} from "./lib/lazyWithRetry";

initSentry();

// The app booted successfully — allow a future guarded reload if a new
// version ships later in this same tab session.
clearChunkReloadFlag();

// Vite emits this when a preloaded chunk 404s (typical after a new deploy
// while the tab was idle). Reload once instead of crashing the route.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadOnceForNewVersion();
});

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    reloadOnceForNewVersion();
  }
});


// Manual browser-side smoke test: append ?sentry_smoke_test=1 to any URL.
// Never runs for normal users.
try {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sentry_smoke_test") === "1") {
      Sentry.captureException(new Error("Sentry browser smoke test"), {
        tags: { smoke_test: "browser" },
      });
      // eslint-disable-next-line no-console
      console.info("[Sentry] browser smoke test dispatched");
    }
  }
} catch (e) {
  // eslint-disable-next-line no-console
  console.warn("[Sentry] smoke test failed", e);
}

createRoot(document.getElementById("root")!).render(
  <SentryErrorBoundary fallback={<FallbackUI />}>
    <App />
  </SentryErrorBoundary>,
);
